/**
 * The one-line summary of what file-integrity monitoring currently covers.
 *
 * Two honesty rules, both from a real screen (mypmp, 2026-07-27):
 *
 *  - Scope slugs are storage keys, not English. Interpolating them raw produced
 *    "Monitoring 5,000 plugins files" — so each scope gets a human label, and an unknown scope
 *    falls back to its slug rather than guessing.
 *  - A mid-walk snapshot is not a settled total. The 5,000 on that screen was exactly the
 *    connector's per-chunk cap, a quarter of the real install — presenting it as "monitoring
 *    5,000 files" implied a coverage that did not exist yet. A scope whose walk cycle is open says
 *    "so far", which is the difference between a claim and a progress report.
 */

export interface FimScopeCoverage {
  scope: string;
  files: number;
  walkInProgress?: boolean;
}

/** Storage slug → the adjective a sentence needs. Unknown scopes keep their slug, undecorated. */
const SCOPE_LABELS: Record<string, string> = {
  core: 'core',
  plugins: 'plugin',
  themes: 'theme',
  uploads: 'upload',
};

export function describeFimCoverage(scopes: readonly FimScopeCoverage[]): string {
  if (scopes.length === 0) return '';
  const parts = scopes.map((s) => {
    const label = SCOPE_LABELS[s.scope] ?? s.scope;
    const base = `${s.files.toLocaleString()} ${label} files`;
    return s.walkInProgress ? `${base} so far (walk in progress)` : base;
  });
  return `Monitoring ${parts.join(' · ')}.`;
}
